import React, { MouseEventHandler, useContext } from 'react';
import Popper from '../../Shared/Popper/Popper';
import AppContext from '../../../contexts/AppContext';
import { useOffline } from '../../../contexts/OfflineContext';
import userApi from '../../../utils/api/user';
import { pendingUploadsWarning } from '../../../utils/backgroundPhotoUploads';

export interface ITenantPopperProps {
  handleClose: MouseEventHandler;
  coordinates: {
    right: number;
    top: number;
  };
  onChange: (division?: number) => void;
  onMFARequired?: (data: { tenantId: number; tenantName: string; email: string }) => void;
}

const TenantPopper: React.FC<ITenantPopperProps> = ({
  handleClose,
  coordinates,
  onChange = null,
  onMFARequired,
}) => {
  const appContext = useContext(AppContext);
  const isOffline = useOffline();
  const user = appContext.get('user');

  const handleClick =
    (tenantId: number, tenantName: string) =>
    async (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      if (isOffline) {
        handleClose(e);
        return;
      }

      // SW2-2458: changeTenant moves the session BEFORE the page navigates, so
      // the leave prompt comes too late: staying would send the remaining
      // uploads under the other tenant, leaving would lose them. Refuse first.
      if (pendingUploadsWarning('switching company')) {
        handleClose(e);

        return;
      }

      const data = await userApi.changeTenant(tenantId);

      if(data.mfa_required){
        // Close the popper and notify parent to show MFA modal
        handleClose(e);
        
        if (onMFARequired && user) {
          onMFARequired({ 
            tenantId, 
            tenantName,
            email: user.email 
          });
        }
        return;
      }

      const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;

      if (startUrl) {
        const url = startUrl.split('//');

        if (url) {
          window.location.href =
            url[0] +
            `//${tenantName}.${url[1]}`;
        }
      }
      if (onChange) {
        onChange();
      }
      handleClose(e);
    };

  return (
    <Popper coordinates={coordinates} handleClose={handleClose}>
      <ul>
        {user &&
          user.tenants.length &&
          user.tenants.map(
            (tenant: { id: number; name: string; domain: string }) => {
              return (
                <li
                  className='pointer'
                  onClick={user.userTenantRoles[0].tenant_id! !== tenant.id ? handleClick(tenant.id, tenant.domain) : undefined}
                  key={tenant.id}
                  value={tenant.id}
                  style={isOffline ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {tenant.name}
                </li>
              );
            },
          )}
      </ul>
    </Popper>
  );
};

export default TenantPopper;
